import type {
  CancelOrderRequest,
  OrderCustomFormToAdd,
  OrderCustomFormToEdit,
  OrderDetail,
  OrderToAdd,
  OrderToEdit,
  PublicGetPublicOrderDataQueryResult,
  PublicOrderToEditByCustomer,
  TableResultExistingOrder,
  TableResultMaterialForCustomForm,
  TableResultOrderAlt,
  TableResultOrderCustomFormResult
} from 'api/models/mainservice';
import { protectedApiClient } from 'api/support/baseApiClients';
import { getUploadHeaders } from 'api/support/helper';
import { apiClient } from 'api/support/unprotectedBaseApiClients';
import type { CustomFormMaterialTableQuery } from 'common/models';
import type { CustomFormsTableQuery } from 'pages/customForms/models';
import type { ExistingOrderQuery, OrderDetailAlt, OrderTableQuery } from 'pages/orders/models';

export default {
  addCustomForm: async (params: { orderId: string; data: OrderCustomFormToAdd }) => {
    const result = await protectedApiClient.post<OrderDetail>(
      `/order/${params.orderId}/customForm`,
      params.data
    );

    return result.data;
  },
  deleteCustomForm: async (params: { orderId: string; customFormId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/order/${params.orderId}/customForm/${params.customFormId}`
    );

    return result.data;
  },
  editCustomForm: async (params: {
    orderId: string;
    customFormId: string;
    data: OrderCustomFormToEdit;
  }) => {
    const result = await protectedApiClient.put<OrderDetail>(
      `/order/${params.orderId}/customForm/${params.customFormId}`,
      params.data
    );

    return result.data;
  },
  saveCustomFormProgress: async (params: {
    orderId: string;
    customFormId: string;
    data: string;
  }) => {
    const result = await protectedApiClient.patch<OrderDetail>(
      `/order/${params.orderId}/customForm/${params.customFormId}/save`,
      { data: params.data }
    );

    return result.data;
  },
  signInReport: async (params: {
    orderId: string;
    customFormId: string;
    reportId: string;
    data: FormData;
  }) => {
    const result = await protectedApiClient.post<OrderDetail>(
      `/order/${params.orderId}/customForm/${params.customFormId}/sign/${params.reportId}`,
      params.data,
      getUploadHeaders
    );

    return result.data;
  },
  getOrderDetail: async (orderId: string) => {
    const result = await protectedApiClient.get<OrderDetail>(`/order/${orderId}`);
    return result.data;
  },
  getSimpleOrderDetail: async (params: {
    orderId: string;
    interventionId: string;
    customFormId: string;
  }) => {
    const result = await protectedApiClient.get<OrderDetail>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm/${params.customFormId}/orderDetail`
    );
    return result.data;
  },
  getPublicOrder: async (tenantCode: string, orderId: string) => {
    const result = await apiClient.get<PublicGetPublicOrderDataQueryResult>(
      `/public/order/${tenantCode}/${orderId}`
    );

    return result.data;
  },
  editPublicOrder: (tenantCode: string, orderId: string, data: PublicOrderToEditByCustomer) => {
    return apiClient.put(`/public/order/${tenantCode}/${orderId}`, data);
  },
  getOrders: async (params: OrderTableQuery) => {
    const result = await protectedApiClient.get<TableResultOrderAlt>(`/v2/order`, { params });
    return result.data;
  },
  addOrder: (order: OrderToAdd) => {
    return protectedApiClient.post<OrderDetailAlt>(`/v2/order/`, order);
  },
  cancelOrder: async (params: { orderId: string; data: CancelOrderRequest }) => {
    const result = await protectedApiClient.put<undefined>(
      `/v2/order/${params.orderId}/Cancel`,
      params.data
    );
    return result.data;
  },
  deleteOrder: async (orderId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/v2/order/${orderId}`);
    return result.data;
  },
  editOrder: async (params: { orderId: string; data: Partial<OrderToEdit> }) => {
    const result = await protectedApiClient.put<OrderDetailAlt>(
      `/v2/order/${params.orderId}`,
      params.data
    );
    return result.data;
  },
  askCustomerForInfo: async (orderId: string) => {
    const result = await protectedApiClient.put<undefined>(
      `/v2/order/${orderId}/AskCustomerForInfo`
    );
    return result.data;
  },
  completeOrder: async (orderId: string) => {
    const result = await protectedApiClient.put<undefined>(`/v2/order/${orderId}/Complete`);
    return result.data;
  },
  reopenOrder: async (orderId: string) => {
    const result = await protectedApiClient.put<undefined>(`/v2/order/${orderId}/reopen`);
    return result.data;
  },
  getOrderDetailV2: async (orderId: string) => {
    const result = await protectedApiClient.get<OrderDetailAlt>(`/v2/order/${orderId}`);
    return result.data;
  },
  submitCustomForm: (orderId: string, customFormId: string, data: string) => {
    return protectedApiClient.post<OrderDetail>(
      `/order/${orderId}/customForm/${customFormId}/submit`,
      { data }
    );
  },
  searchForExisting: async (params: ExistingOrderQuery) => {
    const result = await protectedApiClient.get<TableResultExistingOrder>(
      `/v2/order/searchForExisting/v2`,
      {
        params
      }
    );

    return result.data;
  },
  getCustomForms: async (params: CustomFormsTableQuery) => {
    const result = await protectedApiClient.get<TableResultOrderCustomFormResult>(
      `/order/customForms`,
      { params }
    );
    return result.data;
  },
  getCustomFormMaterial: async (params: {
    orderId: string;
    interventionId: string;
    customFormId: string;
    tableParams: CustomFormMaterialTableQuery;
  }) => {
    const result = await protectedApiClient.get<TableResultMaterialForCustomForm>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm/${params.customFormId}/material`,
      {
        params: params.tableParams
      }
    );

    return result.data;
  }
};
